import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAuth } from '@/core/auth/AuthContext';
import { projectNewService } from '../services/projectNew.service';
import { storageService } from '@/services/storage.service';
import { defaultNewProject, type ProjectNewForm } from '../types/projectNew.types';
import { toast } from 'sonner';

export function useProjectNew() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const [form, setForm] = useState<ProjectNewForm>(defaultNewProject);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  const managersQuery = useQuery({
    queryKey: ['projects1', 'managers', companyId],
    queryFn: () => projectNewService.getManagers(companyId),
    enabled: !!companyId,
  });

  const createMutation = useMutation({
    mutationFn: (data: ProjectNewForm) => projectNewService.createProject(companyId, data),
    onSuccess: () => {
      toast.success('Đã tạo dự án mới thành công');
    },
    onError: (error: any) => {
      toast.error(`Lỗi khi tạo dự án: ${error.message}`);
    }
  });

  const updateForm = (patch: Partial<ProjectNewForm>) => {
    setForm(prev => ({ ...prev, ...patch }));
  };

  const submitProject = async () => {
    setIsUploadingFiles(true);
    try {
      let finalThumbnailUrl = form.thumbnail;
      let finalAttachments = [...(form.attachments || [])];

      // Upload thumbnail
      if (thumbnailFile) {
        const thumbResult = await storageService.uploadFile('media-assets', thumbnailFile, 'project-thumbnails');
        if (thumbResult.error) throw new Error(`Lỗi tải ảnh: ${thumbResult.error}`);
        finalThumbnailUrl = thumbResult.url;
      }

      // Upload attachments
      if (attachmentFiles.length > 0) {
        const results = await storageService.uploadMultiple(attachmentFiles, {
          bucket: 'qa-documents',
          folder: 'project-attachments'
        });
        for (const res of results) {
          if (res.error) throw new Error(`Lỗi tải tài liệu: ${res.error}`);
          finalAttachments.push(res.url);
        }
      }

      const finalForm = { ...form, thumbnail: finalThumbnailUrl, attachments: finalAttachments };
      return await createMutation.mutateAsync(finalForm);
    } finally {
      setIsUploadingFiles(false);
    }
  };

  return {
    form,
    updateForm,
    managers: managersQuery.data || [],
    isLoading: managersQuery.isLoading,
    isSaving: createMutation.isPending || isUploadingFiles,
    createProject: submitProject,
    thumbnailFile,
    setThumbnailFile,
    attachmentFiles,
    setAttachmentFiles,
  };
}
